import { getSettingsLogoUrl } from '../services/settings.service';
import { SettingsLogos } from '../types/settings';
import { FaviconType } from '../types/settings-logo-types';
import { readSettingsClientCache } from './settingsSecureStorage';

const BRANDING_LINK_ATTR = 'data-zeettu-branding';

const FAVICON_LINK_SPECS: {
  type: FaviconType;
  rel: string;
  sizes?: string;
  mimeType?: string;
}[] = [
  { type: 'favicon', rel: 'icon', mimeType: 'image/x-icon' },
  { type: 'favicon-16x16', rel: 'icon', sizes: '16x16', mimeType: 'image/png' },
  { type: 'favicon-32x32', rel: 'icon', sizes: '32x32', mimeType: 'image/png' },
  { type: 'apple-touch-icon', rel: 'apple-touch-icon', mimeType: 'image/png' },
  { type: 'android-chrome-192x192', rel: 'icon', sizes: '192x192', mimeType: 'image/png' },
  { type: 'android-chrome-512x512', rel: 'icon', sizes: '512x512', mimeType: 'image/png' },
];

export async function getCachedSettingsLogos(): Promise<SettingsLogos> {
  const cache = await readSettingsClientCache();
  return cache?.logos ?? cache?.access?.logos ?? {};
}

export function getHeaderLogoUrlFromLogos(logos: SettingsLogos): string {
  const path = logos.header;
  return path ? getSettingsLogoUrl(path) : '';
}

/** A4 letterhead PNG from Settings → Logos (`latterHeader`). */
export function getLatterHeaderLogoUrlFromLogos(logos: SettingsLogos): string {
  const path = logos.latterHeader;
  return path ? getSettingsLogoUrl(path) : '';
}

export async function getCachedLatterHeaderLogoUrl(): Promise<string> {
  const logos = await getCachedSettingsLogos();
  return getLatterHeaderLogoUrlFromLogos(logos);
}

export function applyDocumentBranding(logos: SettingsLogos, documentTitle?: string): void {
  document
    .querySelectorAll(`link[${BRANDING_LINK_ATTR}], meta[${BRANDING_LINK_ATTR}]`)
    .forEach((el) => el.remove());

  for (const spec of FAVICON_LINK_SPECS) {
    const path = logos[spec.type];
    if (!path) continue;
    const href = getSettingsLogoUrl(path);
    const link = document.createElement('link');
    link.setAttribute(BRANDING_LINK_ATTR, 'true');
    link.rel = spec.rel;
    link.href = href;
    if (spec.sizes) link.sizes = spec.sizes;
    if (spec.mimeType) link.type = spec.mimeType;
    document.head.appendChild(link);
  }

  if (documentTitle?.trim()) {
    document.title = documentTitle.trim();
  }

  window.dispatchEvent(new CustomEvent('zeettu-settings-branding'));
}

export async function applyCachedDocumentBranding(): Promise<SettingsLogos> {
  const cache = await readSettingsClientCache();
  const logos = cache?.logos ?? cache?.access?.logos ?? {};
  const title =
    cache?.companyDetails?.tradingName?.trim() ||
    cache?.companyDetails?.legalName?.trim() ||
    undefined;
  applyDocumentBranding(logos, title);
  return logos;
}

/** Company legal name from cached settings (`companyDetails.legalName`). */
export async function getCompanyLegalName(): Promise<string | undefined> {
  const cache = await readSettingsClientCache();
  const name = cache?.companyDetails?.legalName?.trim();
  return name || undefined;
}
